import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { getAccessToken, isTokenExpired } from "@/utils/tokenUtils";

interface ProtectedRouteProps {
    children: React.ReactNode;
    redirectPath?: string;
    isAuthRoute?: boolean; // true: 로그인 필요, false: 로그인 불필요
}

/**
 * 로딩 또는 대기 상태를 표시하는 컴포넌트
 */
const LoadingComponent: React.FC<{ message: string }> = ({ message }) => (
    <div className="flex justify-center items-center min-h-screen">
        <div>{message}</div>
    </div>
);

/**
 * 라우트 보호를 위한 컴포넌트
 * @param children - 보호할 라우트의 컴포넌트
 * @param redirectPath - 로그인된 사용자가 접근할 수 없는 경우 리디렉트할 경로
 * @param isAuthRoute - true: 로그인해야 접근 가능, false: 로그인 없이 접근 가능
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, redirectPath = "/home", isAuthRoute = false }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const navigate = useNavigate();
    const location = useLocation();

    // 로그인 페이지로 리디렉션하는 함수
    const redirectToLogin = useCallback(() => {
        if (location.pathname !== "/auth/login") {
            sessionStorage.setItem("redirectUrl", location.pathname);
            navigate("/auth/login", { replace: true });
        }
    }, [navigate, location.pathname]);

    // 인증 상태 확인
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = getAccessToken();
                const isExpired = token ? await isTokenExpired() : true;
                setIsAuthenticated(!!token && !isExpired);
            } catch (error) {
                console.error("인증 상태 확인 중 오류 발생:", error);
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, []);

    // 인증 상태가 변경될 때 로그인 필요한 페이지 처리
    useEffect(() => {
        if (!isLoading && isAuthRoute && !isAuthenticated) {
            redirectToLogin();
        }
    }, [isLoading, isAuthenticated, isAuthRoute, redirectToLogin]);

    // 조건부 렌더링
    if (isLoading) {
        return <LoadingComponent message="로딩 중..." />;
    }

    // 로그인된 사용자가 로그인 페이지 등에 접근 시
    if (!isAuthRoute && isAuthenticated) {
        return <Navigate to={redirectPath} replace />;
    }

    // 로그인이 필요한 페이지에 비로그인 사용자 접근 시 (useEffect에서 리디렉션 처리됨)
    if (isAuthRoute && !isAuthenticated) {
        return <LoadingComponent message="로그인 페이지로 이동 중..." />;
    }

    // 정상 접근 (인증이 필요한 페이지에 인증된 사용자, 또는 인증이 필요없는 페이지)
    return <>{children}</>;
};

export default ProtectedRoute;
